import { observer } from 'mobx-react-lite'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
	Space,
	ConfigProvider,
	Form,
	Input,
	Card,
	Row,
	Col,
	Tag,
	Tooltip,
	Button,
	Select
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'
import { runInAction, toJS } from 'mobx'
import { IS_NEW } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { SubmitButton } from '../../../components/Form'
import { FormatedTimestamp } from '../../../components/Shared'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css' // Create a CSS file to handle custom styles
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actSaveDocumentation,
	requireDocumentationById
} from '../../../stores/documentation/action'
import MarkdownEditor from '../../../components/Shared/MarkdownEditor'
import { useSmartTranslation } from '../../../cls/TranslationContext'
/**
 * @component DocumentationEdit
 * @description
 * This component renders the edit or view screen for a single documentation entry.
 * It supports both editable and read-only (viewOnly) modes, driven by route or props.
 * Users can input or review the documentation name, description, app type, and Markdown-formatted body.
 *
 * Features include:
 * - Interval-based syncing toggle
 * - Inline Markdown editor for body content
 * - Dynamic field validation
 * - Conditional UI rendering for delete markers
 * - Tour integration for guided onboarding
 *
 * @param {boolean} viewOnly - If true, disables all form fields and hides update functionality.
 */
const DocumentationEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const { users, tourStore } = useStores()
	const { loggedInUserId } = users

	// Fetch the documentation item based on the ID
	const _itm = requireDocumentationById(itmId)

	const [form] = Form.useForm()

	// initial form values
	const _initialFormValues = {
		name: _itm.name,
		description: _itm.description,
		body: _itm.body,
		appType: _itm.appType || ''
	}

	/**
	 * @function _doSave
	 * @description
	 * Handles form submission by syncing the updated form values to the MobX store instance.
	 * Persists changes via `actSaveDocumentation`, and redirects to the documentation list on success.
	 *
	 * @param {Object} vals - The current values of the form fields.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				// Update the item properties based on form input
				_itm.setName(vals.name)
				_itm.setDescription(vals.description)
				_itm.setBody(vals.body)
				_itm.setAppType(vals.appType)
				_itm.setCreatedBy(loggedInUserId)
			})
		}

		console.log('_itm', toJS(_itm))

		// Save the updated documentation and navigate back to the list
		actSaveDocumentation(itmId, toJS(_itm))
			.then(() => {
				navigate('/documentation')
			})
			.catch(console.error)
	}

	/**
	 * Start tour:
	 * This function starts the tour for the report form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		tourStore.startTour('DocumentationEdit-tour', [
			// General info
			...getTourArrayByKeys('info', ['name', 'modified']),

			// General form
			...getTourArrayByKeys('form', ['section'])
		])
	}

	// Determine if the form should be disabled based on the viewOnly prop
	const [formIsDisabled] = React.useState(viewOnly)

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_documentation') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}
								<FormatedTimestamp timestamp={_itm.to_delete} />
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_documentation')}>
								<EyeOutlined id="target-mode" /> {_itm.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new')
						) : (
							<Tooltip title={t('__edit%_documentation')}>
								<SettingOutlined /> {_itm.name}
							</Tooltip>
						)}{' '}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="documentation"
						initialValues={_initialFormValues}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Row gutter={[50, 50]}>
							<Col
								span={24}
								className="section-pattern-dots"
								id="target-form"
							>
								<Space style={{ width: '100%' }}>
									<Form.Item
										label={t('single.name')}
										tooltip={{
											title: t(
												'single.search.description'
											),
											icon: <InfoCircleOutlined />
										}}
										name="name"
										rules={[
											{
												required: true,
												message: t(
													'__provide.please%single.name'
												)
											}
										]}
									>
										<Input
											style={{ minWidth: '150px' }}
											disabled={formIsDisabled}
											showCount
											maxLength={100}
										/>
									</Form.Item>

									<Form.Item
										label={t('single.app-type.label')}
										tooltip={{
											title: t(
												'__select.item%single.app-type.label'
											),
											icon: <InfoCircleOutlined />
										}}
										name="appType"
									>
										<Select
											options={[
												{
													label: t(
														'__select.item%single.app-type.label'
													),
													value: ''
												},
												{
													label: t('_client'),
													value: 'client'
												},
												{
													label: t(
														'single.team.label'
													),
													value: 'team'
												}
											]}
											disabled={formIsDisabled}
										/>
									</Form.Item>
								</Space>

								<Form.Item
									label={t('single.description')}
									tooltip={{
										title: t('single.short-description'),
										icon: <InfoCircleOutlined />
									}}
									name="description"
									rules={[
										{
											required: true,
											message: t(
												'__provide.please%single.description'
											)
										}
									]}
								>
									<Input
										style={{ minWidth: '150px' }}
										disabled={formIsDisabled}
										showCount
										maxLength={200}
									/>
								</Form.Item>

								<Form.Item
									label={t('single.content.label')}
									tooltip={{
										title: t('single.content.description'),
										icon: <InfoCircleOutlined />
									}}
									name="body"
									rules={[
										{
											required: true,
											message: t(
												'__provide.please%single.description'
											)
										}
									]}
								>
									<MarkdownEditor viewOnly={viewOnly} />
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[10, 10]} align="bottom">
							<Col>
								<SubmitButton>
									{_itm.loaded === IS_NEW
										? t('single.create')
										: t('single.update')}
								</SubmitButton>
							</Col>
							{
								// Show  cancel button only if editing resource
								!viewOnly && (
									<Col>
										<Button
											type="default"
											onClick={() => navigate(-1)}
										>
											{t('single.cancel')}
										</Button>
									</Col>
								)
							}
						</Row>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}
DocumentationEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(DocumentationEdit)
